/** Settings → Developers: site API tokens and outgoing webhooks with their delivery log. */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { CopyLine, fmtDate, listOf, toastErr, useSite } from './common';

const { Text } = Typography;

const Once = ({ value, onClose }) => {
    const { t } = useTranslation();
    return (
        <Modal open={!!value} title={t('Copy it now')} onCancel={onClose} onOk={onClose} cancelButtonProps={{ style: { display: 'none' } }} maskClosable={false}>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('It is shown only once.')} />
            <CopyLine value={value || ''} />
        </Modal>
    );
};

const Tokens = ({ scopes }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [secret, setSecret] = useState('');
    const [form] = Form.useForm();
    const load = useCallback(() => api.get('/developers/tokens').then((d) => setItems(listOf(d))).catch(toastErr), [api]);
    useEffect(() => { load(); }, [load]);
    const create = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        try {
            const r = await api.post('/developers/tokens', v);
            setOpen(false); form.resetFields(); setSecret(r.token); load();
        } catch (e) { toastErr(e); }
    };
    return (
        <Card size="small" title={t('API tokens')} extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>{t('Create token')}</Button>}>
            <Table size="small" rowKey="id" pagination={false} dataSource={items} scroll={{ x: 640 }} columns={[
                { title: t('Name'), dataIndex: 'name' },
                { title: t('Token'), dataIndex: 'prefix', render: (v) => <Text code>{v}…</Text> },
                { title: t('Permissions'), dataIndex: 'scopes', render: (v) => (v || []).map((s) => <Tag key={s}>{s}</Tag>) },
                { title: t('Last used'), dataIndex: 'last_used_at', render: (v) => (v ? fmtDate(v) : '—') },
                { title: '', key: 'a', width: 90, render: (_, r) => (r.revoked_at ? <Tag>{t('Revoked')}</Tag> : (
                    <Popconfirm title={t('Revoke this token?')} onConfirm={() => api.del(`/developers/tokens/${r.id}`).then(load).catch(toastErr)}><Button size="small" danger>{t('Revoke')}</Button></Popconfirm>
                )) },
            ]} />
            <Modal open={open} title={t('Create token')} onOk={create} onCancel={() => setOpen(false)}>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input maxLength={100} /></Form.Item>
                    <Form.Item name="scopes" label={t('Permissions')} rules={[{ required: true }]}><Checkbox.Group options={scopes} /></Form.Item>
                </Form>
            </Modal>
            <Once value={secret} onClose={() => setSecret('')} />
        </Card>
    );
};

const Deliveries = ({ hook, onClose }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [items, setItems] = useState([]);
    const load = useCallback(() => { if (hook) api.get(`/developers/webhooks/${hook.id}/deliveries`).then((d) => setItems(listOf(d))).catch(toastErr); }, [api, hook]);
    useEffect(() => { load(); }, [load]);
    return (
        <Modal open={!!hook} width={820} title={t('Deliveries')} footer={null} onCancel={onClose}>
            <Table size="small" rowKey="id" dataSource={items} scroll={{ x: 680 }} pagination={false} columns={[
                { title: t('Event'), dataIndex: 'event' },
                { title: t('Status'), dataIndex: 'status', render: (v) => <Tag color={{ success: 'success', failed: 'error', pending: 'processing' }[v]}>{t({ success: 'Success', failed: 'Failed', pending: 'Pending' }[v])}</Tag> },
                { title: 'HTTP', dataIndex: 'response_code', width: 70 },
                { title: t('Attempts'), dataIndex: 'attempts', width: 80 },
                { title: t('Created'), dataIndex: 'created_at', render: (v) => fmtDate(v) },
                { title: '', key: 'a', width: 100, render: (_, r) => r.status !== 'pending' && <Button size="small" onClick={() => api.post(`/developers/webhooks/${hook.id}/deliveries/${r.id}/redeliver`).then(load).catch(toastErr)}>{t('Resend')}</Button> },
            ]} />
        </Modal>
    );
};

const Webhooks = ({ events }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [secret, setSecret] = useState('');
    const [log, setLog] = useState(null);
    const [form] = Form.useForm();
    const load = useCallback(() => api.get('/developers/webhooks').then((d) => setItems(listOf(d))).catch(toastErr), [api]);
    useEffect(() => { load(); }, [load]);
    const create = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        try {
            const r = await api.post('/developers/webhooks', v);
            setOpen(false); form.resetFields(); setSecret(r.secret); load();
        } catch (e) { toastErr(e); }
    };
    return (
        <Card size="small" title={t('Webhooks')} extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>{t('Add webhook')}</Button>}>
            <Table size="small" rowKey="id" pagination={false} dataSource={items} scroll={{ x: 720 }} columns={[
                { title: 'URL', dataIndex: 'url', ellipsis: true },
                { title: t('Events'), dataIndex: 'events', render: (v) => (v || []).map((s) => <Tag key={s}>{s}</Tag>) },
                { title: t('Active'), dataIndex: 'active', width: 80, render: (v, r) => <Switch size="small" checked={v} onChange={(active) => api.put(`/developers/webhooks/${r.id}`, { active }).then(load).catch(toastErr)} /> },
                { title: '', key: 'a', width: 270, render: (_, r) => (
                    <Space size={4}>
                        <Button size="small" onClick={() => api.post(`/developers/webhooks/${r.id}/test`).then(() => message.success(t('Test event queued'))).catch(toastErr)}>{t('Send test')}</Button>
                        <Button size="small" onClick={() => setLog(r)}>{t('Deliveries')}</Button>
                        <Popconfirm title={t('Replace the signing secret?')} onConfirm={() => api.post(`/developers/webhooks/${r.id}/rotate`).then((x) => setSecret(x.secret)).catch(toastErr)}><Button size="small">{t('New secret')}</Button></Popconfirm>
                        <Popconfirm title={t('Delete this webhook?')} onConfirm={() => api.del(`/developers/webhooks/${r.id}`).then(load).catch(toastErr)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                    </Space>
                ) },
            ]} />
            <Modal open={open} title={t('Add webhook')} onOk={create} onCancel={() => setOpen(false)}>
                <Form form={form} layout="vertical">
                    <Form.Item name="url" label="URL" rules={[{ required: true }]}><Input placeholder="https://" maxLength={500} /></Form.Item>
                    <Form.Item name="events" label={t('Events')} rules={[{ required: true }]}><Checkbox.Group options={events} /></Form.Item>
                </Form>
            </Modal>
            <Once value={secret} onClose={() => setSecret('')} />
            <Deliveries hook={log} onClose={() => setLog(null)} />
        </Card>
    );
};

const ShopDevelopers = () => {
    const { api } = useSite();
    const [meta, setMeta] = useState(null);
    useEffect(() => { api.get('/developers/meta').then(setMeta).catch(toastErr); }, [api]);
    if (!meta) return null;
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <CopyLine value="https://api.yeap.cloud/api/sites/v1" />
            <Tokens scopes={meta.scopes} />
            <Webhooks events={meta.events} />
        </Space>
    );
};

export default ShopDevelopers;
